import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

function Root({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-lg)] border border-border-subtle bg-surface shadow-[var(--shadow-1)]",
        "transition-[border-color,box-shadow] duration-[var(--duration-default)] ease-[var(--ease-out)]",
        "hover:border-border-default hover:shadow-[var(--shadow-2)]",
        className,
      )}
      {...props}
    />
  );
}

interface HeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

function Header({ title, description, action, className, ...props }: HeaderProps) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 border-b border-border-subtle px-4 py-3",
        className,
      )}
      {...props}
    >
      <div className="min-w-0">
        <h3 className="truncate text-sm font-semibold text-fg">{title}</h3>
        {description && (
          <p className="mt-0.5 text-xs text-fg-muted">{description}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

function Body({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-4", className)} {...props} />;
}

function Footer({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 border-t border-border-subtle bg-subtle px-4 py-2.5",
        className,
      )}
      {...props}
    />
  );
}

export const Card = Object.assign(Root, { Header, Body, Footer });
